import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useCart } from "../../context/CartContext";
import {
  Gift,
  Shield,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Star,
  Truck,
} from "lucide-react";
import { db } from "../../firebase/firebaseConfig";
import { getCategory } from "../../constants/categories";
import ProductReviews from "../components/ProductReviews";
import ProductCard from "../components/ProductCard";

const FALLBACK_IMAGE =
  "https://placehold.co/500x500/f5f3f0/6b6b6b?text=Image+indisponible";

const TABS = [
  { id: "description", label: "Description" },
  { id: "ingredients", label: "Ingrédients" },
  { id: "faq", label: "FAQ" },
  { id: "how-to-use", label: "Mode d'emploi" },
];

const formatPrice = (price) => {
  const n = Number(price);
  if (!Number.isFinite(n)) return `${price ?? ""} MAD`;
  return `${n.toLocaleString("fr-MA")} MAD`;
};

function ProductDetails() {
  const { id } = useParams();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeTab, setActiveTab] = useState("description");

  // ── Chargement du produit + produits associés ────────────
  useEffect(() => {
    let cancelled = false;

    const fetchProduct = async () => {
      setLoading(true);
      setError("");
      setProduct(null);
      setRelated([]);

      try {
        const productSnap = await getDoc(doc(db, "products", id));
        if (!productSnap.exists()) throw new Error("Produit introuvable.");

        const prod = { id: productSnap.id, ...productSnap.data() };

        let relatedProducts = [];
        if (prod.category) {
          const relatedSnap = await getDocs(
            query(collection(db, "products"), where("category", "==", prod.category))
          );
          relatedProducts = relatedSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((d) => d.id !== prod.id && d.name)
            .slice(0, 5);
        }

        if (!cancelled) {
          setProduct(prod);
          setSelectedVariant(prod.variants?.[0] ?? null);
          setSelectedImage(0);
          setRelated(relatedProducts);
          setActiveTab("description");
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Impossible de charger le produit.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    } else {
      setError("Identifiant du produit manquant.");
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  // ── Images ───────────────────────────────────────────────
  const images = (() => {
    if (!product) return [];
    const list = [
      product.mainImage || product.image,
      ...(Array.isArray(product.images) ? product.images : []),
    ].filter(Boolean);
    return [...new Set(list)];
  })();

  const currentImage = images[selectedImage] || FALLBACK_IMAGE;

  const changeImage = (direction) => {
    if (images.length < 2) return;
    setSelectedImage((c) => (c + direction + images.length) % images.length);
  };

  // ── Prix et variante ─────────────────────────────────────
  const hasVariants = Array.isArray(product?.variants) && product.variants.length > 0;
  const variantLabel = getCategory(product?.category)?.variantLabel || "Taille";
  const currentPrice = selectedVariant?.price ?? product?.price ?? 0;
  const isSelectionOutOfStock = !!selectedVariant && Number(selectedVariant.stock) === 0;

  const handleAddToCart = () => {
    if (!product || isSelectionOutOfStock) return;

    addToCart({
      id: product.id,
      productId: product.id,
      name: product.name,
      category: product.category,
      image: images[0],
      price: currentPrice,
      variantId: selectedVariant?.id ?? null,
      variantLabel: selectedVariant?.label ?? null,
      quantity: 1,
    });
  };

  // ── États ────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center bg-white">
        <div
          className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#c9a961]"
          role="status"
          aria-label="Chargement du produit"
        />
      </main>
    );
  }

  if (error) {
    return (
      <main className="flex min-h-[50vh] items-center justify-center bg-white px-4 py-16">
        <div className="max-w-md text-center">
          <p className="mb-4 text-base text-red-600">{error}</p>
          <Link
            to="/"
            className="inline-flex rounded-md bg-[#c9a961] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#b8985a]"
          >
            Retour à l'accueil
          </Link>
        </div>
      </main>
    );
  }

  if (!product) return null;

  return (
    <main className="bg-white py-8 text-[#2d2d2d] sm:py-10 lg:py-12">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* Fil d'Ariane */}
        <nav
          aria-label="Fil d'Ariane"
          className="mb-8 flex flex-wrap items-center gap-2 text-xs text-[#6b6b6b] sm:text-sm"
        >
          <Link to="/" className="transition hover:text-[#c9a961]">Accueil</Link>
          <span>/</span>
          <span className="max-w-[180px] truncate">{product.category || "Produits"}</span>
          <span>/</span>
          <span className="max-w-[200px] truncate text-[#2d2d2d]">{product.name}</span>
        </nav>

        {/* SECTION PRINCIPALE */}
        <section className="mb-16 grid grid-cols-1 items-start gap-8 lg:mb-20 lg:grid-cols-2 lg:gap-[60px]">

          {/* GALERIE */}
          <div className="flex min-w-0 flex-col gap-4 sm:gap-5 lg:flex-row lg:items-start">
            {images.length > 1 && (
              <div className="order-2 flex w-full shrink-0 gap-2 overflow-x-auto pb-2 lg:order-1 lg:w-[76px] lg:flex-col lg:overflow-visible lg:pb-0">
                {images.map((image, index) => (
                  <button
                    key={`${image}-${index}`}
                    type="button"
                    onClick={() => setSelectedImage(index)}
                    aria-label={`Afficher l'image ${index + 1}`}
                    aria-pressed={selectedImage === index}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-[#f5f3f0] transition sm:h-[76px] sm:w-[76px] ${
                      selectedImage === index
                        ? "border-[#c9a961] shadow-[0_0_0_2px_rgba(201,169,97,0.2)]"
                        : "border-[#e5e5e5] hover:border-[#c9a961]"
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} - vue ${index + 1}`}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                      className="h-full w-full object-contain p-1"
                    />
                  </button>
                ))}
              </div>
            )}

            <div className="group relative order-1 aspect-square min-w-0 flex-1 overflow-hidden rounded-xl bg-[#f5f3f0] lg:order-2">
              <img
                key={currentImage}
                src={currentImage}
                alt={product.name || "Produit"}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = FALLBACK_IMAGE;
                }}
                className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-[1.03] sm:p-6 lg:p-8"
              />

              {product.category && (
                <span className="absolute left-3 top-3 max-w-[70%] truncate rounded-full border border-white bg-white/95 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-700 shadow-sm sm:left-5 sm:top-5 sm:text-xs">
                  {product.category}
                </span>
              )}

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => changeImage(-1)}
                    aria-label="Image précédente"
                    className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-800 shadow-md transition hover:bg-white sm:left-4 sm:h-11 sm:w-11"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={() => changeImage(1)}
                    aria-label="Image suivante"
                    className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-800 shadow-md transition hover:bg-white sm:right-4 sm:h-11 sm:w-11"
                  >
                    <ChevronRight size={22} />
                  </button>
                  <span className="absolute bottom-3 right-3 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white sm:bottom-4 sm:right-4">
                    {selectedImage + 1} / {images.length}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* INFORMATIONS PRODUIT */}
          <div className="flex min-w-0 flex-col gap-6">
            <div className="border-b border-[#e5e5e5] pb-6">
              {product.category && (
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[1.5px] text-[#c9a961]">
                  {product.category}
                </p>
              )}

              <h1 className="mb-3 font-serif text-2xl font-semibold leading-tight text-[#2d2d2d] sm:text-3xl lg:text-[36px]">
                {product.name}
              </h1>

              {product.sous_description && (
                <p className="mb-4 whitespace-pre-line text-sm text-[#6b6b6b]">
                  {product.sous_description}
                </p>
              )}

              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5 text-[#c9a961]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={15} fill="currentColor" strokeWidth={1} />
                  ))}
                </div>
                <span className="text-xs text-[#6b6b6b]">
                  {product.reviewCount ? `(${product.reviewCount} avis)` : "Aucun avis"}
                </span>
              </div>
            </div>

            {/* PRIX ET VARIANTES */}
            <div className="border-b border-[#e5e5e5] pb-6">
              <div className="mb-5 flex flex-wrap items-baseline gap-3">
                <span className="font-serif text-2xl font-bold text-[#2d2d2d] sm:text-[30px]">
                  {formatPrice(currentPrice)}
                </span>

                {product.originalPrice != null &&
                  Number(product.originalPrice) > currentPrice && (
                    <>
                      <span className="text-sm text-[#6b6b6b] line-through">
                        {formatPrice(product.originalPrice)}
                      </span>
                      <span className="text-xs font-bold text-red-500">
                        -{Math.round((1 - currentPrice / Number(product.originalPrice)) * 100)}%
                      </span>
                    </>
                  )}
              </div>

              {hasVariants && (
                <div className="flex flex-col gap-3">
                  <label className="text-xs font-semibold uppercase tracking-wider">
                    {variantLabel}
                  </label>

                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((variant) => {
                      const isActive = selectedVariant?.id === variant.id;
                      const isOutOfStock = Number(variant.stock) === 0;

                      return (
                        <button
                          key={variant.id}
                          type="button"
                          disabled={isOutOfStock}
                          onClick={() => setSelectedVariant(variant)}
                          aria-pressed={isActive}
                          className={`rounded-md border px-4 py-2 text-xs transition sm:text-sm disabled:cursor-not-allowed disabled:opacity-40 ${
                            isActive
                              ? "border-[#c9a961] bg-[#c9a961] text-white"
                              : "border-[#e5e5e5] text-[#2d2d2d] hover:border-[#c9a961] hover:text-[#b8985a]"
                          }`}
                        >
                          {variant.label}
                          {isOutOfStock && " (épuisé)"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ACTIONS */}
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={isSelectionOutOfStock}
                className="w-full rounded-md bg-[#c9a961] px-6 py-4 text-xs font-bold uppercase tracking-[1px] text-white transition duration-300 hover:-translate-y-0.5 hover:bg-[#b8985a] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40"
              >
                + Ajouter au panier
              </button>

              <Link
                to="/checkout"
                className="flex w-full items-center justify-center rounded-md border border-[#e5e5e5] px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.8px] text-[#2d2d2d] transition hover:border-[#c9a961] hover:bg-[#c9a961]/5 hover:text-[#b8985a]"
              >
                Acheter maintenant
              </Link>
            </div>

            {/* AVANTAGES */}
            <div className="flex flex-col gap-4 border-t border-[#e5e5e5] py-6">
              {[
                { Icon: Gift, text: "Échantillons gratuits" },
                { Icon: Shield, text: "BOJ Rewards Déverrouillés" },
                { Icon: Sparkles, text: "Vegan • Sans Cruauté • Sans Paraben" },
                { Icon: Truck, text: "Livraison et service client" },
              ].map(({ Icon, text }) => (
                <div key={text} className="flex items-center gap-3 text-[13px]">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center text-[#c9a961]">
                    <Icon size={17} />
                  </span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ONGLETS */}
        <section className="mt-16 border-t border-[#e5e5e5] pt-10 sm:mt-20 sm:pt-14">
          <div
            className="mb-8 flex gap-5 overflow-x-auto border-b border-[#e5e5e5] sm:gap-8"
            role="tablist"
            aria-label="Informations sur le produit"
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                id={`tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`panel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`relative shrink-0 py-4 text-[11px] font-semibold uppercase tracking-wider transition sm:text-xs ${
                  activeTab === tab.id
                    ? "text-[#2d2d2d] after:absolute after:bottom-[-1px] after:left-0 after:h-0.5 after:w-full after:bg-[#c9a961]"
                    : "text-[#6b6b6b] hover:text-[#2d2d2d]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            id={`panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
            className="font-body text-sm leading-[1.9] text-[#2d2d2d]"
          >
            {activeTab === "description" && (
              <div className="max-w-4xl space-y-4">
                <p className="whitespace-pre-line">
                  {product.description || "Découvrez les détails de notre produit."}
                </p>
              </div>
            )}

            {activeTab === "ingredients" && (
              <div className="max-w-4xl">
                <h3 className="mb-3 font-sans font-semibold">Ingrédients clés</h3>
                {Array.isArray(product.ingredients) && product.ingredients.length > 0 ? (
                  <ul className="list-inside list-disc space-y-2">
                    {product.ingredients.map((ingredient, i) => (
                      <li key={`${ingredient}-${i}`}>{ingredient}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[#6b6b6b]">
                    Consultez l'emballage du produit pour la liste complète des ingrédients.
                  </p>
                )}
              </div>
            )}

            {activeTab === "faq" && (
              <div className="max-w-4xl space-y-5">
                <div>
                  <h3 className="mb-1 font-sans font-semibold">Convient-il aux peaux sensibles ?</h3>
                  <p>Consultez les indications du fabricant pour vérifier que le produit correspond à votre type de peau.</p>
                </div>
                <div>
                  <h3 className="mb-1 font-sans font-semibold">Puis-je l'utiliser quotidiennement ?</h3>
                  <p>Suivez les recommandations d'utilisation indiquées sur l'emballage du produit.</p>
                </div>
              </div>
            )}

            {activeTab === "how-to-use" && (
              <div className="max-w-4xl">
                {Array.isArray(product.howToUse) && product.howToUse.length > 0 ? (
                  <ol className="list-inside list-decimal space-y-2">
                    {product.howToUse.map((step, i) => (
                      <li key={`${step}-${i}`}>{step}</li>
                    ))}
                  </ol>
                ) : (
                  <p>
                    Respectez les instructions d'utilisation indiquées par le fabricant sur l'emballage du produit.
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* AVIS */}
        <ProductReviews productId={product.id} />

        {/* PRODUITS ASSOCIÉS */}
        {related.length > 0 && (
          <section className="mt-16 border-t-2 border-[#e5e5e5] pb-10 pt-10 sm:mt-20 sm:pb-16 sm:pt-16">
            <h2 className="mb-8 font-serif text-[22px] font-semibold text-[#2d2d2d] sm:text-[28px]">
              Vous aimerez aussi
            </h2>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {related.map((item) => (
                <div key={item.id} className="min-w-0">
                  <ProductCard p={item} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}

export default ProductDetails;